// Phase 4 (docs/plans/INVOICING-IMPROVEMENT-PLAN-2026-07-16.md) — the
// packhouse→grower CHARGES statement: what a grower owes for services.
// Pure ledger assembly from the `invoices` table (single-currency ZAR,
// single-payment model): a DEBIT per issued invoice (dated issued_at) and a
// CREDIT per payment (dated paid_at, amount = the invoice total, since a
// grower invoice is outstanding-or-fully-paid — no partial state).
//
// Two views per the locked decision: OPEN-ITEM (every unsettled invoice,
// aged) and BALANCE-FORWARD (opening balance + period activity + running
// balance). "As-at" is point-in-time so a reprint of last month's statement
// doesn't move.
//
// Ageing is by DUE DATE — days PAST DUE, not days since issue (C3, mig
// 20260814120000, locked decision 7). Ageing from issue was one term-length
// pessimistic: on 30-day terms an invoice issued 29 days ago is nil days
// overdue, but the old basis was about to tip it into "30 days". Invoices
// issued before C3 applied have no stored due_date, so they fall back to
// issued + 30 — not a guess, that is the term the printed footer claimed
// from day one, and the same rule the C3 backfill used.

import { sastYMD } from './dates'

const r2 = (n: number) => Math.round(n * 100) / 100
// Timestamps are stored UTC but the packhouse runs on SAST — an invoice
// issued 23:00 UTC is "tomorrow" locally. Convert every date to its SAST
// calendar day so periods, ageing and the printed date all agree (a bare
// UTC slice mis-buckets late-evening activity). Plain 'YYYY-MM-DD' inputs
// (the date pickers) are unaffected: +2h off UTC-midnight stays the day.
const ymd = (iso: string) => sastYMD(new Date(iso))
const dayMs = (ymdStr: string) => Date.parse(ymdStr + 'T00:00:00Z')

export interface StatementInvoice {
  invoice_id: string
  invoice_number: string
  /** Cutover C4/D-4: the statement is the ONE complete account view, so
   *  transport invoices ride the same ledger. Omitted = grower invoice
   *  (back-compat with existing tests/callers). */
  doc_kind?: 'INVOICE' | 'TRANSPORT'
  issued_at: string            // ISO timestamp (NOT NULL on the table)
  paid_at: string | null       // ISO timestamp or null = outstanding
  payment_reference: string | null
  total: number
  period_from: string          // 'YYYY-MM-DD'
  period_to: string
  due_date?: string | null     // 'YYYY-MM-DD'; absent/null on pre-C3 invoices
}

export type Bucket = 'current' | '30' | '60' | '90'
export interface Ageing { current: number; d30: number; d60: number; d90: number; total: number }

/** Whole days between two dates, on the SAST calendar. */
export function ageDays(dateIso: string, asOfIso: string): number {
  return Math.floor((dayMs(ymd(asOfIso)) - dayMs(ymd(dateIso))) / 86_400_000)
}
export function ageBucketOf(days: number): Bucket {
  if (days < 30) return 'current'
  if (days < 60) return '30'
  if (days < 90) return '60'
  return '90'
}

/** Terms assumed for an invoice issued before C3 stamped due dates. The
 * printed footer has claimed "30 days from invoice date" since the first
 * invoice, so this IS the historical contract — the same number the C3
 * backfill wrote. */
const LEGACY_TERMS_DAYS = 30

/** The SAST calendar day an invoice falls due. Stored `due_date` wins; a
 * pre-C3 invoice falls back to issue + the legacy term. */
export function dueYmdOf(inv: Pick<StatementInvoice, 'issued_at' | 'due_date' | 'doc_kind'>): string {
  // TRANSPORT doctrine (aligned with LedgerTab): no invented terms — a
  // transport doc is due on its issue day, so ageing = age since issue.
  if (inv.doc_kind === 'TRANSPORT') return sastYMD(new Date(inv.issued_at))
  if (inv.due_date) return inv.due_date
  return sastYMD(new Date(dayMs(ymd(inv.issued_at)) + LEGACY_TERMS_DAYS * 86_400_000))
}

/** The term an issued invoice was actually written under, derived from its
 * own two stamps rather than from today's config — re-terming a grower must
 * never rewrite what a document already in their hands claims. */
export function termsDaysOf(issuedAtIso: string, dueYmd: string): number {
  return ageDays(issuedAtIso, dueYmd)
}

/** Bucket by days PAST DUE. Nothing not yet due is overdue, so zero and
 * negative both read as `current` — the distinction the issue-date basis
 * could not make. */
export function overdueBucketOf(daysPastDue: number): Bucket {
  if (daysPastDue <= 0) return 'current'
  if (daysPastDue <= 30) return '30'
  if (daysPastDue <= 60) return '60'
  return '90'
}

const BUCKET_FIELD: Record<Bucket, keyof Ageing> = { current: 'current', '30': 'd30', '60': 'd60', '90': 'd90' }
function sumAgeing(rows: { invoice: StatementInvoice; bucket: Bucket }[]): Ageing {
  const a: Ageing = { current: 0, d30: 0, d60: 0, d90: 0, total: 0 }
  for (const r of rows) {
    a[BUCKET_FIELD[r.bucket]] += r.invoice.total
    a.total += r.invoice.total
  }
  return { current: r2(a.current), d30: r2(a.d30), d60: r2(a.d60), d90: r2(a.d90), total: r2(a.total) }
}

// ---- OPEN ITEM -----------------------------------------------------------
export interface OpenRow {
  invoice: StatementInvoice
  issuedYmd: string
  dueYmd: string
  /** Days PAST DUE as at the statement date. Negative = not yet due. */
  days: number
  bucket: Bucket
}

/** Every invoice unsettled AS AT asOf, aged by days past due. An invoice
 * issued after asOf isn't on the statement yet; one paid on/before asOf is
 * closed. */
export function buildOpenItems(invoices: StatementInvoice[], asOfIso: string): {
  rows: OpenRow[]; ageing: Ageing; totalDue: number
} {
  const asOf = ymd(asOfIso)
  const rows: OpenRow[] = []
  for (const inv of invoices) {
    const issued = ymd(inv.issued_at)
    if (issued > asOf) continue
    if (inv.paid_at && ymd(inv.paid_at) <= asOf) continue
    const dueYmd = dueYmdOf(inv)
    const days = ageDays(dueYmd, asOf)
    rows.push({ invoice: inv, issuedYmd: issued, dueYmd, days, bucket: overdueBucketOf(days) })
  }
  rows.sort((a, b) => a.invoice.issued_at.localeCompare(b.invoice.issued_at))
  const ageing = sumAgeing(rows)
  return { rows, ageing, totalDue: ageing.total }
}

// ---- BALANCE FORWARD -----------------------------------------------------
export type TxnType = 'INVOICE' | 'PAYMENT'
const TXN_ORDER: Record<TxnType, number> = { INVOICE: 0, PAYMENT: 1 }

export interface LedgerTxn {
  date: string; type: TxnType; ref: string; detail: string
  debit: number; credit: number; invoice: StatementInvoice
}
export interface ForwardRow extends LedgerTxn { balance: number }

function txnsOf(invoices: StatementInvoice[]): LedgerTxn[] {
  const txns: LedgerTxn[] = []
  for (const inv of invoices) {
    txns.push({
      date: ymd(inv.issued_at), type: 'INVOICE', ref: inv.invoice_number,
      detail: `${inv.doc_kind === 'TRANSPORT' ? 'Transport invoice' : 'Invoice'} · ${inv.period_from} → ${inv.period_to}`,
      debit: inv.total, credit: 0, invoice: inv,
    })
    if (inv.paid_at) {
      txns.push({
        date: ymd(inv.paid_at), type: 'PAYMENT',
        ref: inv.payment_reference || inv.invoice_number, detail: 'Payment received',
        debit: 0, credit: inv.total, invoice: inv,
      })
    }
  }
  return txns
}

/** Opening balance before `from` + every debit/credit in [from,to] with a
 * running balance; ageing reflects what's still open as at `to`. */
export function buildBalanceForward(invoices: StatementInvoice[], fromIso: string, toIso: string): {
  opening: number; rows: ForwardRow[]; closing: number; ageing: Ageing
} {
  const from = ymd(fromIso), to = ymd(toIso)
  const txns = txnsOf(invoices)
  let opening = 0
  for (const x of txns) if (x.date < from) opening += x.debit - x.credit
  opening = r2(opening)
  const period = txns
    .filter(x => x.date >= from && x.date <= to)
    .sort((a, b) => a.date === b.date ? TXN_ORDER[a.type] - TXN_ORDER[b.type] : a.date.localeCompare(b.date))
  let bal = opening
  const rows: ForwardRow[] = period.map(x => {
    bal = r2(bal + x.debit - x.credit)
    return { ...x, balance: bal }
  })
  const closing = rows.length ? rows[rows.length - 1].balance : opening
  return { opening, rows, closing: r2(closing), ageing: buildOpenItems(invoices, to).ageing }
}
